<template>
	<div class="other_page_r">
		<el-card class="card_box">
			<div slot="header" class="card_title">{{ isEdit ? "编辑活动合集" : "新增活动合集" }}</div>
			<el-form ref="form" :model="form" :rules="rules" label-width="110px" class="collection-form">
				<el-form-item label="合集标题" prop="title">
					<el-input v-model.trim="form.title" maxlength="200" show-word-limit />
				</el-form-item>
				<el-form-item label="活动地点" prop="location_type">
					<el-select v-model="form.location_type">
						<el-option label="线下活动" value="offline" />
						<el-option label="线上活动" value="online" />
					</el-select>
					<el-input v-model.trim="form.location" class="location-input" :placeholder="form.location_type === 'online' ? '请输入线上平台或地址' : '请输入具体活动地点'" />
				</el-form-item>
				<el-form-item label="活动类型" prop="event_category">
					<el-radio-group v-model="form.event_category">
						<el-radio label="web3">Web3</el-radio>
						<el-radio label="ai">AI</el-radio>
					</el-radio-group>
				</el-form-item>
				<el-form-item label="合集封面" prop="cover">
					<div v-if="form.cover" class="cover-uploaded">
						<img :src="form.cover" class="cover-preview" />
						<div class="cover-actions">
							<el-upload action="" :show-file-list="false" :http-request="uploadCover" :before-upload="beforeCoverUpload">
								<el-button size="small">重新上传</el-button>
							</el-upload>
							<el-button size="small" type="danger" plain @click="removeCover">删除</el-button>
						</div>
					</div>
					<el-upload
						v-else
						action=""
						drag
						:show-file-list="false"
						:http-request="uploadCover"
						:before-upload="beforeCoverUpload"
					>
						<i class="el-icon-upload" />
						<div class="el-upload__text">点击或拖拽上传合集封面</div>
					</el-upload>
					<div class="form-tip">推荐比例16:9，JPG/JPEG/PNG/WEBP，最大5MB</div>
				</el-form-item>
				<el-form-item label="合集说明" prop="description">
					<el-input v-model="form.description" type="textarea" :rows="5" maxlength="500" show-word-limit />
				</el-form-item>

				<el-divider content-position="left">合集活动</el-divider>
				<el-form-item label="添加ME活动">
					<el-button type="primary" plain icon="el-icon-plus" @click="openActivitySelector">选择活动</el-button>
					<span v-if="selectedActivities.length" class="selected-tip">已选择 {{ selectedActivities.length }} 个待添加活动</span>
				</el-form-item>
				<el-form-item v-if="selectedActivities.length" label="待添加活动">
					<el-tag v-for="item in selectedActivities" :key="item.id" closable class="activity-tag" @close="removeSelectedActivity(item.id)">
						{{ item.id }} - {{ item.title }}
					</el-tag>
				</el-form-item>
				<el-form-item v-if="activities.length" label="已添加活动">
					<el-table :data="activities" border size="small">
						<el-table-column prop="id" label="ID" width="90" />
						<el-table-column prop="title" label="活动标题" />
						<el-table-column label="操作" width="90">
							<template slot-scope="scope"><el-button type="text" class="danger" @click="removeActivity(scope.row)">移除</el-button></template>
						</el-table-column>
					</el-table>
				</el-form-item>

				<el-form-item>
					<el-button type="primary" :loading="saving" @click="submit">保存</el-button>
					<el-button @click="$router.push('/event/collections')">取消</el-button>
				</el-form-item>
			</el-form>
		</el-card>

		<el-dialog
			title="选择 ME 活动"
			custom-class="activity-selector-dialog"
			:visible.sync="activityDialogVisible"
			width="860px"
			:append-to-body="true"
			:close-on-click-modal="false"
		>
			<div class="selector-search">
				<el-input v-model.trim="activitySearch.admin_keyword" placeholder="输入活动标题或 ID" @keyup.enter.native="searchActivities" />
				<el-select v-model="activitySearch.event_category" placeholder="活动类型">
					<el-option label="全部" value="" />
					<el-option label="Web3" value="web3" />
					<el-option label="AI" value="ai" />
				</el-select>
				<el-button type="primary" @click="searchActivities">查询</el-button>
			</div>
			<el-table ref="activitySelector" v-loading="activityLoading" :data="activityOptions" border @selection-change="handleActivitySelection">
				<el-table-column type="selection" width="55" :selectable="canSelectActivity" />
				<el-table-column prop="id" label="ID" width="90" />
				<el-table-column prop="title" label="活动标题" />
				<el-table-column label="活动类型" width="100">
					<template slot-scope="scope">{{ scope.row.event_category === "ai" ? "AI" : "Web3" }}</template>
				</el-table-column>
				<el-table-column prop="start_time" label="开始时间" width="170" />
			</el-table>
			<pagination :total="activityTotal" :page.sync="activitySearch.page" :limit.sync="activitySearch.size" @pagination="loadActivityOptions" />
			<div slot="footer" class="selector-actions">
				<el-button @click="activityDialogVisible = false">取消</el-button>
				<el-button type="primary" @click="confirmActivitySelection">添加选中活动</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import {
	getEventCollection, createEventCollection, updateEventCollection,
	addCollectionActivities, removeCollectionActivity,
} from "@/api/eventCollections";
import { uploadImage } from "@/api/user";
import { activitylists } from "@/api/article";

export default {
	name: "EventCollectionEdit",
	data() {
		return {
			saving: false,
			activities: [],
			selectedActivities: [],
			activityDialogVisible: false,
			activityLoading: false,
			activityOptions: [],
			activityTotal: 0,
			activitySelection: [],
			activitySearch: { page: 1, size: 10, admin_keyword: "", event_category: "" },
			form: { title: "", location_type: "offline", location: "", event_category: "", cover: "", description: "" },
			rules: {
				title: [{ required: true, message: "请输入合集标题", trigger: "blur" }],
				location_type: [{ required: true, message: "请选择活动地点形式", trigger: "change" }],
				event_category: [{ required: true, message: "请选择活动类型", trigger: "change" }],
				cover: [{ required: true, message: "请上传合集封面", trigger: "blur" }],
				description: [{ required: true, message: "请输入合集说明", trigger: "blur" }],
			},
		};
	},
	computed: { isEdit() { return Boolean(this.$route.query.id); } },
	created() { if (this.isEdit) this.load(); },
	methods: {
		openActivitySelector() {
			this.activityDialogVisible = true;
			this.loadActivityOptions();
		},
		searchActivities() {
			this.activitySearch.page = 1;
			this.loadActivityOptions();
		},
		loadActivityOptions() {
			this.activityLoading = true;
			activitylists(this.activitySearch).then((res) => {
				if (res.code === 200) {
					this.activityOptions = res.data.list || [];
					this.activityTotal = Number(res.data.total || 0);
				}
			}).finally(() => { this.activityLoading = false; });
		},
		canSelectActivity(row) {
			return !this.activities.some(item => Number(item.id) === Number(row.id))
				&& !this.selectedActivities.some(item => Number(item.id) === Number(row.id));
		},
		handleActivitySelection(rows) {
			this.activitySelection = rows;
		},
		confirmActivitySelection() {
			if (!this.activitySelection.length) return this.$message.warning("请先选择活动");
			this.selectedActivities = this.selectedActivities.concat(this.activitySelection.filter(row => this.canSelectActivity(row)));
			this.activityDialogVisible = false;
			this.activitySelection = [];
		},
		removeSelectedActivity(id) {
			this.selectedActivities = this.selectedActivities.filter(item => Number(item.id) !== Number(id));
		},
		beforeCoverUpload(file) {
			const validType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
			if (!validType) this.$message.error("仅支持 JPG、JPEG、PNG、WEBP 格式");
			if (file.size > 5 * 1024 * 1024) this.$message.error("合集封面不能超过 5MB");
			return validType && file.size <= 5 * 1024 * 1024;
		},
		uploadCover(option) {
			const data = new FormData();
			data.append("file", option.file);
			uploadImage(data).then((res) => {
				if (res.code === 200 && res.data && res.data.url) {
					this.form.cover = res.data.url;
					this.$refs.form.clearValidate("cover");
				} else {
					this.$message.error("图片上传失败，请重新上传");
				}
			}).catch(() => this.$message.error("图片上传失败，请重新上传"));
		},
		removeCover() {
			this.form.cover = "";
			this.$nextTick(() => this.$refs.form.validateField("cover"));
		},
		load() {
			getEventCollection(this.$route.query.id).then((res) => {
				if (res.code !== 200) return this.$message.error(res.message);
				Object.keys(this.form).forEach((key) => { this.form[key] = res.data[key] || ""; });
				this.activities = res.data.activities || [];
			});
		},
		submit() {
			this.$refs.form.validate(async (valid) => {
				if (!valid) return;
				if (!this.form.location) return this.$message.error("请输入活动地点");
				this.saving = true;
				try {
					const response = this.isEdit ? await updateEventCollection(this.$route.query.id, this.form) : await createEventCollection(this.form);
					if (response.code !== 200) return this.$message.error(response.message);
					const id = response.data.id;
					if (this.selectedActivities.length) {
						const ids = this.selectedActivities.map(item => item.id).join(",");
						const relation = await addCollectionActivities(id, ids);
						if (relation.code !== 200) return this.$message.error(relation.message);
						const missing = relation.data.missing_ids || [];
						if (missing.length) this.$message.warning(`活动ID ${missing.join("、")} 不存在，其余有效活动已添加`);
					}
					this.$message.success(this.isEdit ? "活动合集更新成功" : "活动合集创建成功");
					this.$router.push("/event/collections");
				} catch (error) { this.$message.error(error.message); }
				finally { this.saving = false; }
			});
		},
		removeActivity(activity) {
			this.$confirm(`确认从当前合集中移除活动「${activity.title}」吗？`, "提示", { type: "warning" })
				.then(() => removeCollectionActivity(this.$route.query.id, activity.id))
				.then((res) => { if (res.code === 200) { this.$message.success("已移除"); this.load(); } else this.$message.error(res.message); })
				.catch((error) => { if (error !== "cancel") this.$message.error(error.message); });
		},
	},
};
</script>

<style scoped>
.collection-form { max-width: 980px; margin: 24px auto; }
.location-input { width: calc(100% - 160px); margin-left: 12px; }
.cover-preview { display: block; width: 480px; max-width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
.cover-actions { display: flex; gap: 10px; margin-top: 12px; }
.form-tip { color: #909399; font-size: 12px; margin-top: 4px; }
.danger { color: #f56c6c; }
.selected-tip { margin-left: 12px; color: #606266; }
.activity-tag { margin: 0 8px 8px 0; max-width: 100%; }
.selector-search { display: flex; gap: 12px; margin-bottom: 16px; }
.selector-search .el-input { flex: 1; }
.selector-actions { display: flex; justify-content: flex-end; align-items: center; gap: 12px; }
.selector-actions .el-button + .el-button { margin-left: 0; }
</style>

<style>
.activity-selector-dialog.el-dialog {
	display: block !important;
	text-align: left;
}
.activity-selector-dialog .el-dialog__header {
	display: block;
	padding: 20px 20px 10px;
	text-align: left;
}
.activity-selector-dialog .el-dialog__body {
	width: auto;
	box-sizing: border-box;
	padding: 20px;
}
.activity-selector-dialog .el-dialog__footer {
	display: block;
	box-sizing: border-box;
	padding: 10px 20px 20px;
	text-align: right;
}
</style>
